import {
  MediaErrorDetail,
  MediaPlayerInstance,
  useMediaState,
} from '@vidstack/react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useVideoPlayerContext } from '../player-context';

type ErrorHandler = (
  error: MediaErrorDetail,
) => Promise<{ canResumePlayback: boolean }>;
type UseErrorHandlerProps = {
  playerRef: React.RefObject<MediaPlayerInstance>;
  onError?: ErrorHandler;
  videoSrc?: string;
};
export const useErrorHandler = ({
  onError,
  videoSrc,
  playerRef,
}: UseErrorHandlerProps) => {
  const { currentTime, moveToTime } = useVideoPlayerContext();
  const error = useMediaState('error', playerRef);
  const [canShowError, setCanShowError] = useState(false);
  const isHandlingErrorRef = useRef(false);

  const handleError = useCallback(async () => {
    if (!error || isHandlingErrorRef.current) {
      return;
    }
    isHandlingErrorRef.current = true;
    const lastPlayedTime = currentTime;
    const { canResumePlayback } = (await onError?.(error)) ?? {
      canResumePlayback: false,
    };

    if (canResumePlayback) {
      moveToTime(lastPlayedTime);
    } else {
      setCanShowError(true);
    }
    isHandlingErrorRef.current = false;
  }, [error, currentTime, onError, moveToTime]);

  useEffect(() => {
    setCanShowError(false);
    isHandlingErrorRef.current = false;
  }, [videoSrc]);

  useEffect(() => {
    if (error && !isHandlingErrorRef.current) {
      handleError();
    }
  }, [error, handleError]);

  return {
    showErrorInUI: canShowError,
    mediaError: error,
  };
};
